import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ExcelService, ExcelSheet } from 'src/app/shared/excel/excel-style.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { keyBy } from 'lodash';
import { BatteryMetricItem, LegendItem } from '../utilization-reports/widgets/utilization-metric-box/utilization-metrics.interface';
import { ChargingType, SiteTemperatureUnit } from '../../../site-settings/site-utilization-settings/site-utilization-settings.interface';
import { SiteDashboardService } from '../../../site-dashboard.service';

@Component({
	selector: 'app-battery-exception-reports',
	templateUrl: './battery-exception-reports.component.html',
	styleUrls: ['./battery-exception-reports.component.css']
})
export class BatteryExceptionReportsComponent implements OnInit, OnDestroy{
	siteId: number = 0;
	batteries: any[] = [];
	utilizationData: any[] = [];
	utilizationEnergyData: any[] = [];
	lowTemperatureBatteriesData: any[] = [];
	highTemperatureBatteriesData: any[] = [];
	lowSOCBatteriesData: any[] = [];
	notPluggedInBatteriesData: any[] = [];
	lowAHData: any[] = [];
	lowWaterLevelData: any[] = [];
	messedEQData: any[] = [];
	potentialSulfatedData: any[] = [];
	potentialWeakCellData: any[] = [];
	siteSub: Subscription = new Subscription();
	siteInfo: any;
	siteBatteriesSettings: any = {};
	widgetsConfig = {
		low_battery_utilization: {
			title: this.translation.instant('exception_reports.low_battery_utilization'),
			tooltip: 'low_battery_utilization_tooltip',
			name: 'low_battery_utilization',
			has_period_filter: true,
			has_voltage_filter: true,
			has_capacity_filter: true,
			has_vintage_filter: true,
			has_manufacturer_filter: true,
			extra_fields: ['utilization_score'],
			value: 0,
			color: '',
		},
		low_battery_energy_usage: {
			title: this.translation.instant('exception_reports.low_battery_energy_usage'),
			tooltip: 'low_battery_energy_usage_tooltip',
			name: 'low_battery_energy_usage',
			has_period_filter: true,
			has_voltage_filter: true,
			has_capacity_filter: true,
			has_vintage_filter: true,
			has_manufacturer_filter: true,
			extra_fields: ['battery_energy_usage_score'],
			value: 0,
			color: '',
		},
		low_ah_percent_return: {
			title: this.translation.instant('exception_reports.low_ah_percent_return'),
			tooltip: 'low_ah_percent_return_tooltip',
			name: 'low_ah_percent_return',
			has_period_filter: true,
			has_voltage_filter: true,
			has_capacity_filter: true,
			has_vintage_filter: true,
			has_manufacturer_filter: true,
			extra_fields: ['ah_return_score'],
			value: 0,
			color: '',
		},
		batteries_outside_geofence: {
			title: this.translation.instant('exception_reports.batteries_outside_geofence'),
			tooltip: 'batteries_outside_geofence_tooltip',
			name: 'batteries_outside_geofence',
			has_period_filter: true,
			has_voltage_filter: true,
			has_capacity_filter: true,
			has_vintage_filter: true,
			has_manufacturer_filter: true,
			extra_fields: ['last_battery_location_long', 'last_battery_location_lat', 'days_outside_geofence'],
			value: 0,
			color: '',
		},
		batteries_with_low_water_level: {
			title: this.translation.instant('exception_reports.batteries_with_low_water_level'),
			tooltip: 'batteries_with_low_water_level_tooltip',
			name: 'batteries_with_low_water_level',
			has_period_filter: true,
			has_voltage_filter: true,
			has_capacity_filter: true,
			has_vintage_filter: true,
			has_manufacturer_filter: true,
			extra_fields: ['low_water_level_days'],
			value: 0,
			color: '',
		},
		batteries_with_messed_equalization_charge: {
			title: this.translation.instant('exception_reports.batteries_with_messed_equalization_charge'),
			tooltip: 'batteries_with_messed_equalization_charge_tooltip',
			name: 'batteries_with_messed_equalization_charge',
			has_period_filter: true,
			has_voltage_filter: true,
			has_capacity_filter: true,
			has_vintage_filter: true,
			has_manufacturer_filter: true,
			extra_fields: ['missed_eq_opportunities'],
			value: 0,
			color: '',
		},
		potential_sulfated_batteries: {
			title: this.translation.instant('exception_reports.potential_sulfated_batteries'),
			tooltip: 'potential_sulfated_batteries_tooltip',
			name: 'potential_sulfated_batteries',
			has_period_filter: true,
			has_voltage_filter: true,
			has_capacity_filter: true,
			has_vintage_filter: true,
			has_manufacturer_filter: true,
			extra_fields: ['low_voltage_charge_cycles'],
			value: 0,
			color: '',
		},
		batteries_with_potential_weak_cells: {
			title: this.translation.instant('exception_reports.batteries_with_potential_weak_cells'),
			tooltip: 'batteries_with_potential_weak_cells_tooltip',
			name: 'batteries_with_potential_weak_cells',
			has_period_filter: true,
			has_voltage_filter: true,
			has_capacity_filter: true,
			has_vintage_filter: true,
			has_manufacturer_filter: true,
			extra_fields: ['low_voltage_discharge_cycles'],
			value: 0,
			color: '',
		},
		low_temperature_batteries: {
			title: this.translation.instant('exception_reports.low_temperature_batteries'),
			name: 'low_temperature_batteries',
			has_period_filter: true,
			has_voltage_filter: true,
			has_capacity_filter: true,
			has_vintage_filter: true,
			has_manufacturer_filter: true,
			extra_fields: ['low_temperature_days'],
			value: 0,
			color: '',
		},
		high_temperature_batteries: {
			title: this.translation.instant('exception_reports.high_temperature_batteries'),
			name: 'high_temperature_batteries',
			has_period_filter: true,
			has_voltage_filter: true,
			has_capacity_filter: true,
			has_vintage_filter: true,
			has_manufacturer_filter: true,
			extra_fields: ['high_temperature_days'],
			value: 0,
			color: '',
		},
		batteries_with_low_soc: {
			title: this.translation.instant('exception_reports.batteries_with_low_soc'),
			tooltip: 'batteries_with_low_soc_tooltip',
			name: 'batteries_with_low_soc',
			has_period_filter: true,
			has_voltage_filter: true,
			has_capacity_filter: true,
			has_vintage_filter: true,
			has_manufacturer_filter: true,
			extra_fields: ['number_of_in_use_cycles'],
			value: 0,
			color: '',
		},
		batteries_with_daily_not_plug_ins: {
			title: this.translation.instant('exception_reports.batteries_with_daily_not_plug_ins'),
			tooltip: 'batteries_with_daily_not_plug_ins_tooltip',
			name: 'batteries_with_daily_not_plug_ins',
			has_period_filter: true,
			has_voltage_filter: true,
			has_capacity_filter: true,
			has_vintage_filter: true,
			has_manufacturer_filter: true,
			extra_fields: ['number_of_not_plugged_in_active_days'],
			value: 0,
			color: '',
		},
	};

	widgetsDataMapper = {
		low_battery_utilization: 'utilizationData',
		low_battery_energy_usage: 'utilizationEnergyData',
		low_ah_percent_return: 'lowAHData',
		batteries_outside_geofence: '',
		batteries_with_low_water_level: 'lowWaterLevelData',
		batteries_with_messed_equalization_charge: 'messedEQData',
		potential_sulfated_batteries: 'potentialSulfatedData',
		batteries_with_potential_weak_cells: 'potentialWeakCellData',
		low_temperature_batteries: 'lowTemperatureBatteriesData',
		high_temperature_batteries: 'highTemperatureBatteriesData',
		batteries_with_low_soc: 'lowSOCBatteriesData',
		batteries_with_daily_not_plug_ins: 'notPluggedInBatteriesData',
	};

	selectedDaysFilter: string = 'allDays';
	siteBatteriesDevices: any = {};

	utilizationLegends: LegendItem[] = [
		{ label: '< 3%', severity: 'Low' },
		{ label: '3 – 5%', severity: 'Moderate' },
		{ label: '5 – 10%', severity: 'High' },
		{ label: '> 10%', severity: 'Critical' }
	];

	assetUtilization: Array<{ label: string; value: number, showPercentage: boolean, percentage: number }> = [];
	maintenance: Array<{ label: string; value: number, showPercentage: boolean, percentage: number }> = [];
	charging: Array<{ label: string; value: number, showPercentage: boolean, percentage: number }> = [];

	// filters
	readonly periods = [
		{ value: 'last_7_days', label: this.translateService.instant('durations.last_7_days') },
		{ value: 'last_14_days', label: this.translateService.instant('durations.last_14_days') },
		{ value: 'last_30_days', label: this.translateService.instant('durations.last_30_days') },
		{ value: 'last_90_days', label: this.translateService.instant('durations.last_90_days') },
		{ value: 'year_to_date', label: this.translateService.instant('durations.year_to_date') },
		{ value: 'last_year', label: this.translateService.instant('durations.last_year') },
		{ value: 'since_installation', label: this.translateService.instant('durations.all_years') },
	];
	readonly dataModeValues = {
		CURRENT_STATUS: 'current_status',
		HISTORICAL: 'historical',
	};
	readonly yesterdayPeriod = 'yesterday';

	readonly dataModes = [
		{ value: this.dataModeValues.CURRENT_STATUS, label: this.translateService.instant('exception_reports.current_status') },
		{ value: this.dataModeValues.HISTORICAL, label: this.translateService.instant('exception_reports.historical_data') },
	];

	batteryCapacitiesOptions: any[] = [];
	batteryManufacturerOptions: any[] = [];
	batteryVoltagesOptions: any[] = [];
	vintageOptionsDropdown = this.siteDashboardService.vintageOptionsDropdown;

	filters: {
		selectedBatteryVoltage: string;
		selectedBatteryCapacity: string;
		selectedBatteryManufacturer: string;
		selectedPeriod: string;
		selectedBatteryVintage: string[];
		dataMode: string;
	} = {
			selectedBatteryCapacity: 'all',
			selectedBatteryVoltage: 'all',
			selectedBatteryManufacturer: 'all',
			selectedPeriod: this.periods[0].value,
			selectedBatteryVintage: [this.siteDashboardService.vintageOptionsDropdown[0].value],
			dataMode: this.dataModes[1].value,
		};

	showFastOpportunityWidget: boolean = false;

	noData: boolean = true;

	PERCENT_WIDGETS = new Set([
		'low_battery_utilization',
		'low_battery_energy_usage',
		'low_ah_percent_return',
	]);

	constructor (
		private translation: TranslateService,
		private siteService: SitesService,
		private route: ActivatedRoute,
		private sideMenuService: SideMenuService,
		public commonService: CommonService,
		private translateService: TranslateService,
		private excelService: ExcelService,
		public siteDashboardService: SiteDashboardService,
	) {}

	ngOnInit() {
		this.siteId = this.route.parent.snapshot.params.siteId || 0;

		if (!this.siteId)
			return;

		this.siteSub = this.sideMenuService.currentSite.subscribe((site) => {
			this.commonService.setPageTabTitle(`${site['customer_name']} - ${site['name']} - ${this.translation.instant('exception_reports.battery_exception_reports')}`)
			this.siteInfo = site;
		});

		this.getSiteUtilizationSettings();
		this.getBatteryExceptionReports();
	}

	getSiteUtilizationSettings() {
		this.siteService.getSiteUtilizationSettings(this.siteId).subscribe(res => {
			this.siteBatteriesSettings = res[0].batteries_utilization_settings || {};
			this.showFastOpportunityWidget = this.siteBatteriesSettings?.charging_type == ChargingType.OPPORTUNITY_FAST;
		});
	}

	getBatteryExceptionReports() {
		this.siteService.getBatteryExceptionReports(this.siteId).subscribe((response: any) => {
			this.batteries = response.devices || [];
			this.utilizationData = response.utilizationData || [];
			this.utilizationEnergyData = response.utilizationEnergyData || [];
			this.lowAHData = response.lowAHData || [];
			this.lowWaterLevelData = response.lowWaterLevelData || [];
			this.messedEQData = response.messedEQData || [];
			this.potentialSulfatedData = response.potentialSulfatedData || [];
			this.potentialWeakCellData = response.potentialWeakCellData || [];
			this.lowTemperatureBatteriesData = response.lowTemperatureBatteriesData || [];
			this.highTemperatureBatteriesData = response.highTemperatureBatteriesData || [];
			this.lowSOCBatteriesData = response.lowSOCBatteriesData || [];
			this.notPluggedInBatteriesData = response.notPluggedInBatteriesData || [];

			// prepare filter options
			const batteriesCapacities = this.batteries.map(b => b.battery_capacity);
			const batteriesVoltages = this.batteries.map(b => b.battery_voltage);
			const batteriesManufacturers = this.batteries.map(b => b.battery_manufacturer);

			this.batteryVoltagesOptions = this.commonService.prepareOptions(batteriesVoltages);
			this.batteryCapacitiesOptions = this.commonService.prepareOptions(batteriesCapacities);
			this.batteryManufacturerOptions = this.commonService.prepareOptions(batteriesManufacturers);
			this.siteBatteriesDevices = keyBy(response.devices || [], 'mac_address');
			if (Object.keys(this.siteBatteriesDevices).length)
				this.noData = false;

			this.formatSelectedData();

		});
	}

	onSelectionChanged(event) {
		switch (event.filter) {
			case 'period':
				this.filters.selectedPeriod = event.value;
				break;
			case 'voltage':
				this.filters.selectedBatteryVoltage = event.value;
				break;
			case 'capacity':
				this.filters.selectedBatteryCapacity = event.value;
				break;
			case 'manufacturer':
				this.filters.selectedBatteryManufacturer = event.value;
				break;
			case 'batteryVintage':
				this.batteryVintageSelectionChanged();
				break;
			case 'dataMode':
				this.filters.dataMode = event.value;

				this.filters.selectedPeriod = this.periods[0].value;
				if (this.filters.dataMode === this.dataModeValues.CURRENT_STATUS)
					this.filters.selectedPeriod = this.yesterdayPeriod;

				break;
			default:
				break;
		}

		this.filters = structuredClone(this.filters);
		this.formatSelectedData();
	}

	exportReports() {
		const data = this.prepareDataForExcelExport();
		this.excelService.exportExcel(data, `Exception Report.xlsx`);
	}

	prepareDataForExcelExport(): ExcelSheet[] {
		const devices = keyBy(this.batteries, 'mac_address');

		const csvHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.siteInfo?.customer_name,
			siteName: this.siteInfo?.name,
			includeSpacing: false
		}) || [];

		const headerInfoBlock: any[][] = [
			...csvHeaderInfo,
			[this.translateService.instant('exception_reports.time_period'), this.getSelectedPeriodLabel()],
			[this.translateService.instant('exception_reports.selected_filter_values')],
			...this.getSelectedFilterValuesRows(),
			[],
		];

		const sheetData: any[][] = [...headerInfoBlock];

		const headerInfoRows: number[] = [];
		headerInfoBlock.forEach((row, idx) => {
			if (row && row.length) headerInfoRows.push(idx);
		});

		const titleRows: number[] = [];
		const tables: any[] = [];

		let counter = 0;
		for (const widgetKey in this.widgetsDataMapper) {
			const widgetDataKey = this.widgetsDataMapper[widgetKey];
			const widgetConfig = this.widgetsConfig[widgetKey];
			const widgetData = this[widgetDataKey]?.[this.filters.selectedPeriod];

			const staticFields = [
				this.translateService.instant('device_performance.battery_id'),
				this.translateService.instant('device_performance.battery_sn'),
				this.translateService.instant('exception_reports.battery_manufacturer'),
				this.translateService.instant('exception_reports.battery_voltage'),
				this.translateService.instant('devices.battery_capacity'),
			];

			const header = [
				...staticFields,
				...widgetConfig.extra_fields.map((field: string) => this.translateService.instant(`exception_reports.${field}`))
			];

			if(counter) //ignore extra empty row in the first
				sheetData.push([]);

			const titleRowIndex = sheetData.length;
			sheetData.push([widgetConfig.title]);
			titleRows.push(titleRowIndex);

			const headerRowIndex = sheetData.length;
			sheetData.push(header);

			const dataStartRow = sheetData.length;

			const percentColumns = this.getPercentColumnIndexes(widgetKey, staticFields.length);

			const widgetDevicesData = widgetData || devices;

			for (const macAddress in widgetDevicesData) {
				const device = devices[macAddress] || {};

				if (!this.isIncludedDevice(macAddress) || (widgetData?.[macAddress] && !widgetData?.[macAddress]?.showDevice))
					continue;

				if (widgetKey == 'batteries_outside_geofence' && !device.is_far_device)
					continue;

				const recordRow: any[] = [
					device.battery_id,
					device.battery_sn,
					device.battery_manufacturer,
					device.battery_voltage,
					device.battery_capacity,
				];

				if (widgetData) {
					recordRow.push(this.normalizePercentValue(widgetKey, widgetData[macAddress].value));
				} else {
					for (const field of widgetConfig.extra_fields) {
						if (field == 'days_outside_geofence' && device.is_estimated_location)
							recordRow.push(this.translateService.instant('g.n/a'));
						else if (field == 'days_outside_geofence')
							recordRow.push(Math.round(device[field] / (60 * 60 * 24)));
						else
							recordRow.push(device[field]);
					}
				}

				sheetData.push(recordRow);
			}

			const dataEndRow = sheetData.length - 1;

			if (dataEndRow >= dataStartRow) {
				tables.push({
					headerRow: headerRowIndex,
					dataStartRow,
					dataEndRow,
					startCol: 0,
					endCol: header.length - 1,
					percentColumns,
				});
			} else {
				// No data: header stays bold, add a "No data" row
				const noDataRowIndex = sheetData.length;
				const noDataRow = new Array(header.length).fill('');
				noDataRow[0] = this.translateService.instant('g.no_data_to_show');
				sheetData.push(noDataRow);

				tables.push({
					headerRow: headerRowIndex,
					dataStartRow: noDataRowIndex,
					dataEndRow: noDataRowIndex,
					startCol: 0,
					endCol: header.length - 1,
					percentColumns: [],
					noData: true,
				});
			}

			counter++;
		}

		return [{
			data: sheetData,
			sheetName: this.translateService.instant('exception_reports.battery_exception_reports'),
			style: {
				headerInfoRows,
				titleRows,
				tables,
			},
		}];
	}

	getSelectedPeriodLabel(): string {
		return this.periods.find(p => p.value === this.filters.selectedPeriod)?.label || this.filters.selectedPeriod;
	}

	getSelectedFilterValuesRows(): any[][] {
		const allLabel = this.translateService.instant('g.all');

		const voltage = this.filters.selectedBatteryVoltage === 'all'
			? allLabel : this.filters.selectedBatteryVoltage;
		const capacity = this.filters.selectedBatteryCapacity === 'all'
			? allLabel : this.filters.selectedBatteryCapacity;
		const manufacturer = this.filters.selectedBatteryManufacturer === 'all'
			? allLabel : this.filters.selectedBatteryManufacturer;

		const vintage = (this.filters.selectedBatteryVintage || [])
			.map(v => this.vintageOptionsDropdown.find(o => o.value === v)?.label || v)
			.join(', ') || allLabel;

		return [
			[this.translateService.instant('exception_reports.battery_voltage'), voltage],
			[this.translateService.instant('devices.battery_capacity'), capacity],
			[this.translateService.instant('exception_reports.battery_manufacturer'), manufacturer],
			[this.translateService.instant('devices.battery_vintage'), vintage],
		];
	}

	getPercentColumnIndexes(widgetKey: string, staticFieldCount: number): number[] {
		if (!this.PERCENT_WIDGETS.has(widgetKey))
			return [];

		return [staticFieldCount];
	}

	normalizePercentValue(widgetKey: string, value: any): any {
		if (!this.PERCENT_WIDGETS.has(widgetKey) || value == null || isNaN(value))
			return value;

		const num = Number(value);
		return num / 100;
	}

	formatSelectedData() {
		const assetUtilizationLabels = [
			'low_battery_utilization',
			'low_battery_energy_usage',
			'low_ah_percent_return',
			'batteries_outside_geofence'
		];

		for (const score of assetUtilizationLabels) {
			this.widgetsConfig[score].value = this.calculateScore(score);
			this.widgetsConfig[score].severity = this.getSeverity(this.widgetsConfig[score].value);
		}

		this.assetUtilization = assetUtilizationLabels.map(label => ({
			label: this.translation.instant(`utilization_metrics.${label}`),
			value: this.widgetsConfig[label].value,
			percentage: this.widgetsConfig[label].value,
			showPercentage: true,
			name: label, 
		}));

		this.calculateMaintenanceMetrics();
		this.calculateChargingMetrics();
	}

	calculateScore(chartType: string): number {
		// utilization/energy/AH data are only populated for batteries installed during the selected
		// period (DAO/siteDAO.js getBatteryExceptionReports), so the denominator has to be scoped the
		// same way - counting every site battery here would dilute the % with not-yet-installed ones.
		switch (chartType) {
			case 'low_battery_utilization': {
				const selectedData = this.utilizationData?.[this.filters.selectedPeriod];
				const totalInstalledDevicesCount = Object.keys(selectedData || {})
					.filter(macAddress => this.isIncludedDevice(macAddress)).length;

				let totalLowBatteryUtilizationDevices = 0;

				for (const macAddress in selectedData) {
					if (this.isIncludedDevice(macAddress) && selectedData[macAddress].showDevice)
						totalLowBatteryUtilizationDevices++;
				}

				return totalInstalledDevicesCount
					? +((totalLowBatteryUtilizationDevices / totalInstalledDevicesCount) * 100).toFixed(2) : 0;
			}

			case 'low_battery_energy_usage': {
				const selectedData = this.utilizationEnergyData?.[this.filters.selectedPeriod];
				const totalInstalledDevicesCount = Object.keys(selectedData || {})
					.filter(macAddress => this.isIncludedDevice(macAddress)).length;

				let totalLowBatteryEnergyDevices = 0;

				for (const macAddress in selectedData) {
					if (this.isIncludedDevice(macAddress) && selectedData[macAddress].showDevice)
						totalLowBatteryEnergyDevices++;
				}

				return totalInstalledDevicesCount ? +((totalLowBatteryEnergyDevices / totalInstalledDevicesCount) * 100).toFixed(2) : 0;
			}

			case 'low_ah_percent_return': {
				const selectedData = this.lowAHData?.[this.filters.selectedPeriod];
				const totalInstalledDevicesCount = Object.keys(selectedData || {})
					.filter(macAddress => this.isIncludedDevice(macAddress)).length;

				let totalLowAhDevices = 0;

				for (const macAddress in selectedData) {
					if (this.isIncludedDevice(macAddress) && selectedData[macAddress].showDevice)
						totalLowAhDevices++;
				}

				return totalInstalledDevicesCount ? +((totalLowAhDevices / totalInstalledDevicesCount) * 100).toFixed(2) : 0;
			}
			case 'batteries_outside_geofence': {
				// ST-2689: only batteries with a usable location (GPS or estimated) are judged against the
				// geofence, so the % is the flagged ones out of those - the same rows the report table shows.
				const locatedDevices = this.batteries.filter(device => device.has_location && this.isIncludedDevice(device.mac_address));
				const totalOutsideGeofenceDevices = locatedDevices.filter(device => device.is_far_device).length;

				return locatedDevices.length ? +((totalOutsideGeofenceDevices / locatedDevices.length) * 100).toFixed(1) : 0;
			}
			default:
			return 0;
		}
	}

	getSeverity(value: number): BatteryMetricItem["severity"] {
		switch (true) {
			case value > 10:
				return "Critical";
			case value > 5:
				return "High";
			case value >= 3:
				return "Moderate";
			default:
				return "Low";
		}
	}

	isIncludedDevice(macAddress: string) {
		if (this.filters.selectedBatteryVoltage !== 'all' && this.siteBatteriesDevices?.[macAddress]?.battery_voltage !== this.filters.selectedBatteryVoltage)
			return false;

		if (this.filters.selectedBatteryCapacity !== 'all' && this.siteBatteriesDevices?.[macAddress]?.battery_capacity !== this.filters.selectedBatteryCapacity)
			return false;

		if (this.filters.selectedBatteryManufacturer !== 'all' && this.siteBatteriesDevices?.[macAddress]?.battery_manufacturer !== this.filters.selectedBatteryManufacturer)
			return false;

		if (!this.siteDashboardService.isVintageWithinFilter(this.siteBatteriesDevices?.[macAddress], this.filters.selectedBatteryVintage))
			return false;

		return true;
	}

	calculateMaintenanceMetrics() {
		const maintenanceLabels = [
			'batteries_with_low_water_level',
			'batteries_with_messed_equalization_charge',
			'potential_sulfated_batteries',
			'batteries_with_potential_weak_cells'
		];
		
		for (const score of maintenanceLabels) {
			const widgetConfig = this.widgetsConfig[score];
			const widgetDataKey = this.widgetsDataMapper[score];
			const widgetData = this[widgetDataKey]?.[this.filters.selectedPeriod];

			// widgetData is only populated for batteries installed during the selected period
			// (DAO/siteDAO.js getBatteryExceptionReports), so the denominator has to be scoped the
			// same way - counting every site battery here would dilute the % with not-yet-installed ones.
			const totalInstalledDevicesCount = Object.keys(widgetData || {}).filter(macAddress => this.isIncludedDevice(macAddress)).length;

			let includedDevicesCount = 0;
			for (const macAddress in widgetData) {
				if (this.isIncludedDevice(macAddress) && widgetData[macAddress].value && widgetData[macAddress].showDevice)
					includedDevicesCount++;
			}

			widgetConfig.value = totalInstalledDevicesCount ? +((includedDevicesCount / totalInstalledDevicesCount) * 100).toFixed(1) : 0;
			widgetConfig.severity = this.getSeverity(widgetConfig.value);
		}

		this.maintenance = maintenanceLabels.map(label => ({
			label: this.translation.instant(`utilization_metrics.${label}`),
			value: this.widgetsConfig[label].value,
			percentage: this.widgetsConfig[label].value,
			showPercentage: true,
			name: label,
		}));

	}

	calculateChargingMetrics() {
		const chargingLabels = [
			'batteries_with_daily_not_plug_ins',
			'batteries_with_low_soc',
			'high_temperature_batteries'
		];

		for (const score of chargingLabels) {
			const widgetConfig = this.widgetsConfig[score];
			const widgetDataKey = this.widgetsDataMapper[score];
			const widgetData = this[widgetDataKey]?.[this.filters.selectedPeriod];

			// widgetData is only populated for batteries installed during the selected period
			// (DAO/siteDAO.js getBatteryExceptionReports), so the denominator has to be scoped the
			// same way - counting every site battery here would dilute the % with not-yet-installed ones.
			const totalInstalledDevicesCount = Object.keys(widgetData || {}).filter(macAddress => this.isIncludedDevice(macAddress)).length;

			let includedDevicesCount = 0;
			for (const macAddress in widgetData) {
				if (this.isIncludedDevice(macAddress) && widgetData[macAddress].value && widgetData[macAddress].showDevice)
					includedDevicesCount++;
			}

			widgetConfig.value = totalInstalledDevicesCount ? +((includedDevicesCount / totalInstalledDevicesCount) * 100).toFixed(1) : 0;
			widgetConfig.severity = this.getSeverity(widgetConfig.value);
		}

		this.charging = chargingLabels.map(label => ({
			label: this.prepareMetricFieldName(label),
			value: this.widgetsConfig[label].value,
			showPercentage: true,
			percentage: this.widgetsConfig[label].value,
			name: label,
		}));
		this.noData = [this.charging, this.assetUtilization, this.maintenance].every(arr => arr?.every(item => item.value === 0));
	}

	prepareMetricFieldName(field: string): string {
		let name = '';

		switch (field) {
			case 'batteries_with_daily_not_plug_ins':
				name = `${this.translation.instant(`utilization_metrics.${field}`)} (< ${this.siteBatteriesSettings.min_daily_plug_ins})`;
				break;
			case 'batteries_with_low_soc':
				name = `${this.translation.instant(`utilization_metrics.${field}`)} (< ${this.siteBatteriesSettings.min_daily_battery_soc}%)`;
				break;
			case 'high_temperature_batteries':
				name = `${this.translation.instant(`utilization_metrics.${field}`)} (> ${this.getTemp(this.siteBatteriesSettings.temperature_unit)})`;
				break;
			default:
				break;
		}

		return name;
	}

	getTemp(unit: number) {
		let temp;
		if(unit == SiteTemperatureUnit.CELSIUS) {
			temp = this.commonService.fahToCel(this.siteBatteriesSettings.high_temperature_limit);
			return `${temp}°C`;
		}
		else {
			temp = this.siteBatteriesSettings.high_temperature_limit;
			return `${temp}°F`;
		}
	}

	batteryVintageSelectionChanged() {
		if (!this.filters.selectedBatteryVintage.length)
			this.filters.selectedBatteryVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];

		if (this.filters.selectedBatteryVintage.length > 1 && this.filters.selectedBatteryVintage.includes(this.siteDashboardService.vintageOptionsDropdown[0].value)) {
			if (this.filters.selectedBatteryVintage[0] == this.siteDashboardService.vintageOptionsDropdown[0].value)
				this.filters.selectedBatteryVintage = this.filters.selectedBatteryVintage.filter(value => value !== this.siteDashboardService.vintageOptionsDropdown[0].value);
			else
				this.filters.selectedBatteryVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];
		}
	}

	ngOnDestroy() {
		if (this.siteSub)
			this.siteSub.unsubscribe();
	}
}